// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IsNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cartLineSharedStyles from "@tps/App/SharedStyles/CartLineSharedStyles";
import { color, hideInputNumberArrows, mergeWithCss, xClassNames } from "@tps/XDL/Utilities";
import { maxLengthAttr } from "@tps/XDL/Utilities/validation";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

type ButtonType = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">;

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    showActionButton?: boolean;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    maxLength?: number;
    minValue?: number;
    maxValue?: number;
    showLabel?: boolean;
    editable?: boolean;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    countButton?: ButtonPresentationProps;
    increaseButton?: ButtonPresentationProps;
    decreaseButton?: ButtonPresentationProps;
    countIcon?: IconPresentationProps;
    qtyLabel?: TypographyProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const qtyFieldStyles: QtyFieldStyles = {
    wrapper: {
        css: css`
            display: flex;
            max-width: 94px;
            width: 100%;
            max-height: 28px;
            position: relative;
            border: 1px solid ${color("common.ui5")};

            &.qty-disabled {
                opacity: 0.5;
            }

            &:not(.qty-disabled):hover {
                border-color: ${color("common.ui8")};
            }

            &:not(.qty-disabled):has(input[data-test-selector="cartline_qty"]:focus) {
                border-color: ${color("common.ui9")};
            }

            &:not(.qty-disabled).show-action-button {
                & > ${FormFieldStyle} {
                    padding-left: 20px;
                    padding-right: 20px;
                }
            }
        `,
    },
    editableQuantityTextField: {
        mergeCss: true,
        backgroundColor: "transparent",
        sizeVariant: "small",
        labelProps: {
            mergeCss: true,
            css: css`
                display: none;
                &&& + ${/* sc-selector */ FormInputWrapper} {
                    margin-top: 0;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                &&& input:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                    &,
                    &:active,
                    &:hover,
                    &:focus {
                        outline: none;
                        border: 0;
                        box-shadow: none;
                        padding: 0;
                        height: 24px;
                        font-size: 14px;
                        line-height: 1;
                        &:not(:disabled) {
                            border: 0;
                            box-shadow: none;
                        }
                    }
                }
            `,
            inputSelect: css`
                font-size: 16px;
                line-height: 17px;
                text-align: center;
                padding: 0;
                &:disabled {
                    background-color: transparent;
                }

                ${hideInputNumberArrows}
            `,
        },
    },
    countButton: {
        variant: "tertiary",
        sizeVariant: "small",
        css: css`
            position: absolute;
            top: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 24px;
            flex: 0 0 24px;
            min-height: 24px;
            padding: 0;
            border: 0;
            background-color: transparent;
            &:hover:not(:disabled) {
                background-color: transparent;
                ${IconWrapper} {
                    color: ${color("common.ui8")};
                }
            }
        `,
    },
    countIcon: {
        color: "common.ui4",
        size: 14,
        mergeCss: true,
    },
    qtyLabel: {
        ...cartLineSharedStyles.qtyLabel,
    },
    readOnlyQuantityText: {
        ...cartLineSharedStyles.extendedUnitNetPriceText,
    },
};

qtyFieldStyles.decreaseButton = mergeWithCss(qtyFieldStyles.countButton, {
    css: css`
        left: 0;
    `,
});

qtyFieldStyles.increaseButton = mergeWithCss(qtyFieldStyles.countButton, {
    css: css`
        right: 0;
    `,
});

const enterKey = 13;

const QtyField: FC<Props> = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    showActionButton = true,
    allowZero = false,
    minValue,
    maxValue,
    maxLength,
    showLabel = true,
    editable = true,
    ...qtyFieldsProps
}) => {
    const [styles] = useState(() => mergeWithCss(qtyFieldStyles, extendedStyles));
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.currentTarget.value = !event.currentTarget.value ? "" : event.currentTarget.value;
        const maximumLength = maxLength || maxLengthAttr.qty;
        const maximumValue = maxValue !== undefined ? maxValue : 9999;
        const minimumValue = minValue !== undefined ? minValue : 1;

        if (Number.isNaN(Number(event.currentTarget.value))) {
            return;
        }

        if (
            event.currentTarget.value.length > maximumLength ||
            Number(event.currentTarget.value) > maximumValue ||
            Number(event.currentTarget.value) < minimumValue
        ) {
            event.currentTarget.value = `${currentValue}`;
        }

        onChangeTextField?.(event);
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        /**
         * We skip this validation when a user click on increase/decrease button
         * In this case, event.currentTarget will be equal null
         */
        if (event?.currentTarget) {
            event.currentTarget.value = Number(event.currentTarget.value || "0") as any;
            onChangeHandler(event);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - 1 >= 0
                ? Number(currentValue) - 1
                : undefined;

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }

        event.currentTarget.value = `${nextValue}`;
        event.target.value = `${nextValue}`;

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }
        const maximumLength = maxLength || maxLengthAttr.qty;

        const nextValue = ((!allowZero && currentValue) || (allowZero && currentValue !== undefined)) &&
            IsNumeric(`${currentValue}`) &&
            `${Number(currentValue) + 1}`.length <= maximumLength &&
            Number(currentValue) < (maxValue || 9999)
                ? Number(currentValue) + 1
                : undefined;

        if (!nextValue) {
            return;
        }

        event.currentTarget.value = `${nextValue}`;
        event.target.value = `${nextValue}`;

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }

        onChangeHandler(event as any);
        setCallOnSubmitSafety(true);
    };

    return (
        <>
            {editable ? (
                <StyledWrapper>
                    {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                    <StyledWrapper
                        {...styles.wrapper}
                        data-test-selectore="quantity-field"
                        className={xClassNames.append([
                            disabled && "qty-disabled",
                            showActionButton && "show-action-button",
                        ])}
                    >
                        {showActionButton && (
                            <Button
                                {...styles.decreaseButton}
                                disabled={disabled}
                                onClick={onDecreaseClickHandler}
                                aria-label="qty-decrement"
                            >
                                <Icon {...styles.countIcon} src={Minus} />
                            </Button>
                        )}
                        <TextField
                            type="text"
                            maxLength={maxLength || maxLengthAttr.qty}
                            disabled={disabled}
                            value={currentValue}
                            onChange={onChangeHandler}
                            onBlur={onBlurHandler}
                            onClick={onClickHandler}
                            onKeyDown={onKeyDownHandler}
                            min={minValue !== undefined ? minValue : 1}
                            max={maxValue || 9999}
                            label={labelOverride || translate("QTY")}
                            {...styles.editableQuantityTextField}
                            {...qtyFieldsProps}
                        />
                        {showActionButton && (
                            <Button
                                {...styles.increaseButton}
                                disabled={disabled || Number(currentValue) === (maxValue || 9999)}
                                onClick={onIncreaseClickHandler}
                                aria-label="qty-increment"
                            >
                                <Icon {...styles.countIcon} src={Plus} />
                            </Button>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <SmallHeadingAndText
                    {...styles.readOnlyQuantityText}
                    heading={(labelOverride as string) || translate("QTY")}
                    text={currentValue as string | number}
                    data-test-selector="quoteDetail_cardLine_qty"
                />
            )}
        </>
    );
};

export default QtyField;
